import { ToastId, useToast } from '@chakra-ui/react';
import { groupBy, sortBy, uniqueId } from 'lodash';
import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useMutation, useQuery } from 'urql';
import { useFlag } from '@unleash/proxy-client-react';
import { useBreadcrumb } from '../components/common/BreadcrumbProvider';
import { useProtanProjectSteps } from '../components/common/ProtanProjectStepsProvider';
import { useSheetViewer } from '../components/common/SheetViewer';
import { MoveOrCopySheetShapes } from '../components/orderContractor/pdf-shapes/MoveOrCopySheetShapes';
import {
  DrainLineDrawingResult,
  InitialShapeDrawingState,
  LineStringShapeDrawingResult,
  PointDrawingResult,
  PolygonShapeDrawingResult,
  ShapeDrawing,
  ShapeDrawingMode,
  ShapeDrawingResult,
  ShapeDrawingResultType,
  SlopedInsulationCrossSection,
  WedgesDrawingResult,
} from '../components/orderContractor/pdf-shapes/SheetShapeDrawing';
import {
  Diamond,
  KileTypeSKU,
} from '../components/orderContractor/pdf-shapes/protan/SheetWedgeShapeDrawing';
import { isShapeSelected } from '../components/orderContractor/ShapesPanel';
import { getSheetShapeUrn } from '../components/viewers/common/hooks/useSheetShapesManager';
import {
  toMultiPolygon2,
  toPoints2,
} from '../domain/geometry/algorithms/util/type-mapping';
import { Point2 } from '../domain/geometry/geometric-types';
import {
  BulkUpsertSparkelPropertiesDocument,
  CreateOrderEntryFromTemplateDocument,
  CreateOrderEntryFromTemplateInput,
  CreateSheetShapeDocument,
  CreateSheetShapeInput,
  EventResource,
  EventType,
  ForgeAttributeQueryTypeInput,
  GetOrderEntryTemplatesPageDocument,
  GetSheetShapesForProjectDeepDocument,
  MultiPolygon2Type,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
  SheetShapeDeepFragment,
  UpdateSheetShapeDocument,
  UpdateSheetShapeInput,
} from '../gql/graphql';
import { useUserTenant } from '../services/auth-info';
import { EventName, useMixpanel } from '../services/mixpanel';
import { GeometricResultVisualization } from '../services/viewer/result-visualisation/GeometricResultVisualization';
import { useSelection } from '../services/viewer/selection';
import { findNextAvailableNameForCopy } from '../utils/naming-utils';
import { useAutoGeneratedRoof as useAutoGeneratedRoof } from './autogenerated-roofs-provider';
import { useDefaultAlgorithmVisualizations } from './default-algorithm-visualizations';
import { useResolvedOrder } from './order';
import { useOrderLabelsProtan } from './protan-data';
import {
  setProtanProperties,
  updateDrainLineProps,
  updateWedgeProps,
  updateWedgePropsV2,
} from './protan-special-handling';
import { useShapes } from './shapes';
import { useEventHistory } from './event-history';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { handleProtanFlow } from 'src/components/orderContractor/ProtanSheetShapesToolbar';
import { kileChoices } from 'src/components/orderContractor/ProductChoices';
import { getCrossSectionFromProperties } from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
import { useInsulationHandler } from 'src/components/orderContractor/pdf-shapes/protan/hooks/useInsulationHandler';
import { ViewerMode } from 'src/components/common/viewer-mode';
import { useProjectHistory } from 'src/components/project/project-history/hooks/useProjectHistory';
import { getHistoricalShapes } from 'src/components/project/project-history/hooks/useDiffing';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';

type SheetShapeDrawingState =
  | {
      isDrawing: false;
      mode: undefined;
      hasResult: false;
      minHeight?: undefined;
    }
  | {
      isDrawing: true;
      mode: ShapeDrawingMode;
      // Opted for only exposing this to the outside world for now, but feel free to add the actual result if needed
      hasResult: boolean;
      minHeight?: undefined;
    }
  | {
      isDrawing: true;
      mode: ShapeDrawingMode.DrainLine;
      hasResult: boolean;
      minHeight: number;
    };

export type SheetShapesContextValue = {
  drawing: SheetShapeDrawingState & {
    activate: (mode?: ShapeDrawingMode) => void;
    editShape: (shapeToEdit: string) => void;
    moveOrCopyShapes: (
      shapeIds: string[],
      translationVector: Point2,
      shouldCopy?: boolean
    ) => void;
    deactivate: () => void;
    submit: (skipProtanFlow?: boolean) => Promise<void>;
    submitError: Error | undefined;
    isSubmitting: boolean;
    shapeToEdit: string | null;
    isMovingShapes: boolean;
    setMovingShapes: (isMoving: boolean) => void;
    isCopyingShapes: boolean;
    setCopyingShapes: (isCopying: boolean) => void;
    setMinHeight: (minHeight: number) => void;
  };
  shapes: {
    data: SheetShapeDeepFragment[] | undefined;
    fetching: boolean;
    error: Error | undefined;
  };
};

const SheetShapesContext = createContext<SheetShapesContextValue | null>(null);

// eslint-disable-next-line complexity
export const SheetShapesProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { projectId } = useParams<{ projectId: string }>() as {
    projectId: string;
  };

  const [{ data: shapesData, error: shapesError, fetching: shapesFetching }] =
    useQuery({
      query: GetSheetShapesForProjectDeepDocument,
      variables: { projectId },
    });

  const [createShapeResult, createShapeMutation] = useMutation(
    CreateSheetShapeDocument
  );
  const [updateShapeResult, updateShapeMutation] = useMutation(
    UpdateSheetShapeDocument
  );

  const { tenant, isProtan } = useUserTenant();
  const { getNewShapeName } = useShapes(projectId);

  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();

  const shapes = useMemo(() => {
    // When in history mode, use historical shapes
    if (isHistoryDrawerOpen && referenceState && diff) {
      const historicalShapes = getHistoricalShapes(
        referenceState,
        diff,
        shapesData?.project?.sheetShapes
      ).filter(
        (shape) => shape.__typename === 'SheetShape'
      ) as SheetShapeDeepFragment[];
      return sortBy(historicalShapes, (shape) => shape.createdAt);
    }

    // Normal mode - use current shapes from database
    return shapesData?.project?.sheetShapes
      ? sortBy(shapesData?.project?.sheetShapes, (shape) => shape.createdAt)
      : null;
  }, [
    shapesData?.project?.sheetShapes,
    isHistoryDrawerOpen,
    referenceState,
    diff,
  ]);

  const internalShapeRef = useRef(shapes);

  useEffect(() => {
    internalShapeRef.current = shapes;
  }, [shapes]);

  const { operationResults } = useDefaultAlgorithmVisualizations(shapes || []);

  const [drawingModeKey, setDrawingModeKey] = useState(uniqueId());
  const [minHeight, setMinHeight] = useState(0.26);

  const toast = useToast();

  const {
    visibilityManager: visibilityManagerV1,
    viewerRef,
    pageNumber: pageNumberV1,
    activeSheetId: activeSheetIdV1,
    shapesManager: shapesManagerV1,
    isDrawing: isDrawingShapeV1,
    setIsDrawing: setDrawingShape,
    ghosting: { setGhostMode },
    calibration: { calibration },
    propertyData,
  } = useSheetViewer();
  const {
    activeSheetId: activeSheetIdV2,
    pagination: { pageNumber: pageNumberV2 },
    shapesManager: shapesManagerV2,
    visibilityManager: visibilityManagerV2,
    activeTool,
    setActiveTool,
    drawing: sheetsViewerV2Drawing,
  } = useSheetViewerV2();
  const isDrawingShape = activeTool === ActiveTool.Drawing || isDrawingShapeV1;
  const { sheetsViewerMode: viewerMode } = useEnabledSheetViewerModeValue();
  const pageNumber =
    viewerMode === ViewerMode.Sheets ? pageNumberV1 : pageNumberV2;
  const activeSheetId =
    viewerMode === ViewerMode.Sheets ? activeSheetIdV1 : activeSheetIdV2;

  const renderSheetShapes =
    viewerMode === ViewerMode.Sheets
      ? shapesManagerV1.renderSheetShapes
      : shapesManagerV2.renderSheetShapes;
  const unrenderSheetShapes =
    viewerMode === ViewerMode.Sheets
      ? shapesManagerV1.unrenderSheetShapes
      : shapesManagerV2.unrenderSheetShapes;
  const { hideSheetShape, showSheetShape, addToIsolation, isolatedElements } =
    viewerMode === ViewerMode.Sheets
      ? visibilityManagerV1
      : visibilityManagerV2;

  const { deselectDbIds } = useSelection();

  const [shapeIdToEdit, setShapeIdToEdit] = useState<string | null>(null);

  const [isMovingShapes, setMovingShapes] = useState(false);
  const [isCopyingShapes, setCopyingShapes] = useState(false);
  const [capturedShapesForMove, setCapturedShapesForMove] = useState<
    SheetShapeDeepFragment[]
  >([]);
  const [drawingMode, setDrawingMode] = useState<ShapeDrawingMode>(
    ShapeDrawingMode.Rectangle
  );
  const [drawingResult, setDrawingResult] = useState<ShapeDrawingResult | null>(
    null
  );
  const [drawingInitialState, setDrawingInitialState] =
    useState<InitialShapeDrawingState[typeof drawingMode]>();

  const { t } = useTranslation('project');

  const shapeToEdit = useMemo(
    () => shapes?.find((shape) => shape.id === shapeIdToEdit),
    [shapes, shapeIdToEdit]
  );

  const isSubmitting = createShapeResult.fetching || updateShapeResult.fetching;

  const hasDrawingResult = !!drawingResult && drawingResult.valid;

  const isMovingOrCopyingToastRef = useRef<ToastId>();

  const [, bulkCreateSparkelProps] = useMutation(
    BulkUpsertSparkelPropertiesDocument
  );

  const {
    qkast,
    fireRequirements,
    energyRequirements,
    substrate,
    buildingHeight: roofHeight,
  } = useOrderLabelsProtan();

  const enableWedgeDrawingV2 = useFlag('enable-wedge-drawing-v2');

  const activateDrawing = useCallback(
    // eslint-disable-next-line complexity
    (newMode?: ShapeDrawingMode) => {
      setDrawingShape(true);

      if (newMode) {
        setDrawingMode(newMode);
        if (
          (newMode === ShapeDrawingMode.MagicPolygon ||
            newMode === ShapeDrawingMode.Polygon) &&
          drawingResult?.type === ShapeDrawingResultType.Polygon &&
          drawingResult.valid
        ) {
          setDrawingInitialState({
            multipolygon: drawingResult.multipolygon,
          });
        } else if (
          newMode === ShapeDrawingMode.DrainLine &&
          drawingResult?.type === ShapeDrawingResultType.DrainLine &&
          drawingResult.valid
        ) {
          const defaultMinHeight =
            drawingResult?.crossSection?.minHeight ?? 0.26;

          setMinHeight(defaultMinHeight);
          setDrawingInitialState({
            points: drawingResult.drainLines.flatMap((segment) => [
              [segment[0][0], segment[0][1]],
              [segment[1][0], segment[1][1]],
            ]),
            crossSection: {
              ...drawingResult.crossSection,
              minHeight: defaultMinHeight,
            } as SlopedInsulationCrossSection,
          });
        } else {
          setDrawingInitialState(undefined);
          setDrawingResult(null);
        }
      }
    },
    [drawingResult, setDrawingShape, setDrawingInitialState]
  );

  const deactivateDrawing = useCallback(() => {
    setDrawingShape(false);
    setDrawingResult(null);
    setDrawingInitialState(undefined);
    setDrawingModeKey(uniqueId());
  }, [setDrawingShape]);

  // Helper function to check if a shape is a wedge shape
  const isWedgeShape = (shapeName: string, points: Point2[]): boolean => {
    return (
      shapeName === 'Wedge' ||
      shapeName.toLowerCase().includes('wedge') ||
      points.length > 2
    );
  };

  const editShape = useCallback(
    (shapeId: string) => {
      const shapeToEdit = internalShapeRef.current?.find(
        (shape) => shape.id === shapeId
      );
      if (!shapeToEdit) {
        throw new Error('Shape not found');
      }
      // Deselect to hide the shape being edited, because selected elements are visible even when hidden
      deselectDbIds({ [shapeToEdit.urn]: [shapeToEdit.dbId] });
      if (viewerMode === ViewerMode.SheetsV2) {
        setActiveTool(ActiveTool.Drawing);
        sheetsViewerV2Drawing.editShape(shapeToEdit);
      } else if (shapeToEdit.sheetShapePolygon) {
        setDrawingMode(ShapeDrawingMode.Polygon);
        setDrawingInitialState({
          multipolygon: toMultiPolygon2(
            shapeToEdit.sheetShapePolygon.multipolygon
          ),
        });
      } else if (shapeToEdit.sheetShapeLine) {
        // Check if this is a wedge shape by examining its name or points length
        // For Protan users, we assume lines with more than 2 points are wedge shapes
        const points = toPoints2(shapeToEdit.sheetShapeLine.points);

        if (isProtan && isWedgeShape(shapeToEdit.name, points)) {
          setDrawingMode(ShapeDrawingMode.Wedges);
          setDrawingInitialState({
            points: points,
          });
        } else {
          setDrawingMode(ShapeDrawingMode.LineString);
          setDrawingInitialState({
            points: points,
          });
        }
      } else if (shapeToEdit.sheetShapePoint) {
        setDrawingMode(ShapeDrawingMode.Point);
        setDrawingInitialState({
          point: [
            [
              shapeToEdit.sheetShapePoint.point.x,
              shapeToEdit.sheetShapePoint.point.y,
            ],
          ],
          updating: true,
        });
      } else if (shapeToEdit.sheetShapeSplitLine) {
        const crossSection = getCrossSectionFromProperties(
          propertyData,
          shapeToEdit.dbId,
          shapeToEdit.urn
        );
        setDrawingMode(ShapeDrawingMode.DrainLine);
        setDrawingInitialState({
          points: toPoints2(shapeToEdit.sheetShapeSplitLine.points),
          crossSection,
        });
        setMinHeight(crossSection?.minHeight ?? 0.26);
      } else {
        throw new Error('Shape type not supported');
      }
      setDrawingShape(true);
      setShapeIdToEdit(shapeId);
    },
    [
      deselectDbIds,
      propertyData,
      setDrawingShape,
      isProtan,
      viewerMode,
      setActiveTool,
      sheetsViewerV2Drawing,
    ]
  );

  const [, createOrderEntryFromTemplate] = useMutation(
    CreateOrderEntryFromTemplateDocument
  );

  let { orderId } = useParams<{
    orderId: string;
  }>() as { orderId: string };

  const [{ data: resourceTemplates }] = useQuery({
    query: GetOrderEntryTemplatesPageDocument,
  });

  const order = useResolvedOrder(projectId, orderId);

  const handleCreateFromTemplateClick = useCallback(
    async (
      templateId: string,
      title: string,
      query: ForgeAttributeQueryTypeInput | undefined,
      orderId: string
    ) => {
      const input: CreateOrderEntryFromTemplateInput = {
        orderEntryTemplateId: templateId,
        orderId,
        title,
        color: '#FFFFFF',
        query,
        dbids: null,
      };
      const result = await createOrderEntryFromTemplate({ input });
      if (result.error) {
        console.error(result.error);
        toast({
          status: 'error',
          position: 'top',
          title: 'An error occurred creating item from template',
        });
      }
    },
    [createOrderEntryFromTemplate, toast]
  );

  const { logEvent } = useEventHistory(projectId ?? '');

  const updateShape = useCallback(
    // eslint-disable-next-line complexity
    async () => {
      invariant(shapeToEdit, 'No shape is being edited');
      invariant(drawingResult, 'No drawing result');
      const input = getUpdateShapeInput(shapeToEdit, drawingResult);

      const result = await updateShapeMutation({
        input,
      });

      // Protan object updates
      if (
        isProtan &&
        result.data?.updateSheetShape?.sheetShape &&
        order?.resolvedOrder?.orderEntries &&
        calibration?.calibration
      ) {
        if (
          // Protan drain line update
          drawingResult.type === ShapeDrawingResultType.DrainLine &&
          drawingResult.crossSection &&
          drawingResult.roofShapeDbId
        ) {
          const unitScale = getUnitScaleFromCalibration(
            calibration?.calibration
          );

          await updateDrainLineProps(
            result.data.updateSheetShape.sheetShape,
            drawingResult.crossSection,
            drawingResult.roofShapeDbId,
            bulkCreateSparkelProps,
            handleCreateFromTemplateClick,
            resourceTemplates,
            order.resolvedOrder.orderEntries,
            propertyData,
            unitScale,
            projectId,
            orderId,
            substrate ?? ''
          );
        } else if (
          // Protan wedge update
          drawingResult.type === ShapeDrawingResultType.Wedge &&
          drawingResult.diamond &&
          drawingResult.kileType &&
          enableWedgeDrawingV2
        ) {
          await updateWedgePropsV2(
            result.data.updateSheetShape.sheetShape,
            drawingResult.diamond,
            drawingResult.kileType,
            drawingResult.totalWedgeProductLength,
            bulkCreateSparkelProps,
            projectId,
            handleCreateFromTemplateClick,
            resourceTemplates,
            order.resolvedOrder.orderEntries,
            orderId
          );
        }
      }

      if (result.error) {
        toast({
          status: 'error',
          title: 'Error',
          description: 'Could not update shape. Please try again',
        });
        throw result.error;
      } else {
        toast({
          title: `Updated shape ${shapeToEdit.name}`,
          status: 'success',
        });
        deactivateDrawing();
        setShapeIdToEdit(null);

        logEvent({
          eventName: 'Shape Updated',
          eventType: EventType.Modified,
          resourceName: shapeToEdit.name,
          resourceType: EventResource.SheetShape,
          resolved: false,
          projectId,
        });

        if (
          result.data?.updateSheetShape?.sheetShape?.dbId &&
          Object.values(isolatedElements).flat().length > 0
        ) {
          addToIsolation({
            [result.data.updateSheetShape.sheetShape.urn]: [
              result.data.updateSheetShape.sheetShape.dbId,
            ],
          });
        }

        logEvent({
          eventName: 'Shape Updated',
          eventType: EventType.Modified,
          resourceName: shapeToEdit.name,
          resourceType: EventResource.SheetShape,
          metadata: {
            affectedResources: [shapeToEdit.id],
          },
        });
      }
    },
    [
      addToIsolation,
      bulkCreateSparkelProps,
      calibration?.calibration,
      deactivateDrawing,
      drawingResult,
      enableWedgeDrawingV2,
      handleCreateFromTemplateClick,
      isProtan,
      isolatedElements,
      logEvent,
      order?.resolvedOrder?.orderEntries,
      orderId,
      projectId,
      propertyData,
      resourceTemplates,
      shapeToEdit,
      substrate,
      toast,
      updateShapeMutation,
    ]
  );

  const { projectSteps, setStepStatus } = useProtanProjectSteps();

  const roofOutlineInProgress =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.RoofOutline
    )?.status === ProtanProjectStepStatusEnum.InProgress;

  const drainLinePlacementInProgress =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainLinePlacement
    )?.status === ProtanProjectStepStatusEnum.InProgress;

  // eslint-disable-next-line complexity
  const createShape = useCallback(
    // eslint-disable-next-line complexity
    async (
      shapeInput: CreateSheetShapeInput,
      diamond?: Diamond | Diamond[],
      kileType?: KileTypeSKU | KileTypeSKU[],
      unitScale?: number
    ) => {
      invariant(shapes, 'Shapes must be defined');
      invariant(drawingResult, 'Shapes must be defined');
      invariant(activeSheetId, 'Shapes must be defined');

      const shapeName = getNewShapeName(
        tenant?.group ?? '',
        drawingResult.type
      );

      const result = await createShapeMutation({ input: shapeInput });

      if (
        result.data?.createSheetShape?.sheetShape &&
        drawingResult.type === ShapeDrawingResultType.Wedge
      ) {
        if (enableWedgeDrawingV2) {
          await updateWedgePropsV2(
            result.data.createSheetShape.sheetShape,
            drawingResult.diamond,
            drawingResult.kileType,
            drawingResult.totalWedgeProductLength,
            bulkCreateSparkelProps,
            projectId,
            handleCreateFromTemplateClick,
            resourceTemplates,
            order?.resolvedOrder?.orderEntries,
            orderId
          );
        } else {
          if (
            diamond &&
            !Array.isArray(diamond) &&
            kileType &&
            !Array.isArray(kileType)
          ) {
            updateWedgeProps(
              result.data,
              diamond,
              kileType,
              bulkCreateSparkelProps,
              projectId
            );
          } else {
            console.error('Diamond or kileType is undefined');
          }
        }
      } else if (
        result.data?.createSheetShape?.sheetShape &&
        drawingResult.type === ShapeDrawingResultType.DrainLine &&
        drawingResult.crossSection &&
        drawingResult.roofShapeDbId &&
        order?.resolvedOrder?.orderEntries &&
        unitScale
      ) {
        await updateDrainLineProps(
          result.data.createSheetShape.sheetShape,
          drawingResult.crossSection,
          drawingResult.roofShapeDbId,
          bulkCreateSparkelProps,
          handleCreateFromTemplateClick,
          resourceTemplates,
          order.resolvedOrder.orderEntries,
          propertyData,
          unitScale,
          projectId,
          orderId,
          substrate ?? ''
        );
      }

      if (isProtan && result.data?.createSheetShape?.sheetShape) {
        setProtanProperties(
          result.data,
          roofHeight,
          qkast,
          orderId,
          fireRequirements,
          energyRequirements,
          bulkCreateSparkelProps,
          projectId
        );
      }

      if (result.error) {
        toast({
          status: 'error',
          title: 'Error',
          description: 'Could not create shape. Please try again',
        });
        throw result.error;
      } else {
        if (!toast.isActive(`shape ${shapeName} created`)) {
          toast({
            id: `shape ${shapeName} created`,
            status: 'success',
            duration: 3000,
            title: `Created shape ${shapeName}`,
          });
        }
        logEvent({
          eventName: 'Shape Created',
          eventType: EventType.Created,
          resourceName: drawingResult.type,
          resourceType: EventResource.SheetShape,
          resolved: false,
          projectId,
        });
        if (!drainLinePlacementInProgress) deactivateDrawing();
        if (
          result.data?.createSheetShape?.sheetShape?.dbId &&
          Object.values(isolatedElements).flat().length > 0
        ) {
          addToIsolation({
            [result.data.createSheetShape.sheetShape.urn]: [
              result.data.createSheetShape.sheetShape.dbId,
            ],
          });
        }

        const shapeType = drawingResult.type;

        logEvent({
          eventName: 'Shape Created',
          eventType: EventType.Created,
          resourceName: drawingResult.type,
          resourceType: EventResource.SheetShape,
          metadata: {
            affectedResources: [result.data?.createSheetShape?.sheetShape?.id],
            details: `Created ${shapeType} shape ${result.data?.createSheetShape?.sheetShape?.name}`,
          },
        });
      }
    },
    [
      activeSheetId,
      addToIsolation,
      bulkCreateSparkelProps,
      createShapeMutation,
      deactivateDrawing,
      drainLinePlacementInProgress,
      drawingResult,
      enableWedgeDrawingV2,
      energyRequirements,
      fireRequirements,
      getNewShapeName,
      handleCreateFromTemplateClick,
      isProtan,
      isolatedElements,
      logEvent,
      order?.resolvedOrder?.orderEntries,
      orderId,
      projectId,
      propertyData,
      qkast,
      resourceTemplates,
      roofHeight,
      shapes,
      substrate,
      tenant?.group,
      toast,
    ]
  );

  /* eslint-disable complexity */
  const createShapes = useCallback(async () => {
    invariant(shapes, 'Shapes must be defined');
    invariant(drawingResult, 'Shapes must be defined');
    invariant(activeSheetId, 'Shapes must be defined');

    const shapeName = getNewShapeName(tenant?.group ?? '', drawingResult.type);

    const input = getCreateShapeInput(
      drawingResult,
      projectId,
      activeSheetId,
      pageNumber,
      shapeName,
      enableWedgeDrawingV2
    );

    if (
      // V1 wedge drawing
      drawingResult.type === ShapeDrawingResultType.Wedge &&
      Array.isArray(input) &&
      !enableWedgeDrawingV2
    ) {
      input.forEach((shape, index) =>
        createShape(
          shape,
          drawingResult.diamond[index],
          drawingResult.kileType[index]
        )
      );

      const uniqueKileSkus = new Set(drawingResult.kileType);

      uniqueKileSkus.forEach((sku) => {
        if (order.resolvedOrder?.orderEntries)
          kileChoices(
            sku,
            orderId,
            handleCreateFromTemplateClick,
            resourceTemplates,
            order.resolvedOrder.orderEntries
          );
      });

      return;
    } else if (
      drawingResult.type === ShapeDrawingResultType.Wedge &&
      enableWedgeDrawingV2
    ) {
      console.log('enableWedgeDrawingV2');
    } else if (
      drawingResult.type === ShapeDrawingResultType.DrainLine &&
      !Array.isArray(input)
    ) {
      createShape(
        input,
        undefined,
        undefined,
        calibration?.calibration
          ? getUnitScaleFromCalibration(calibration?.calibration)
          : undefined
      );
      return;
    }

    if (Array.isArray(input)) {
      input.forEach((shape) => createShape(shape));
    } else {
      await createShape(input);
    }
  }, [
    shapes,
    drawingResult,
    activeSheetId,
    getNewShapeName,
    tenant?.group,
    projectId,
    pageNumber,
    createShape,
    order.resolvedOrder?.orderEntries,
    orderId,
    handleCreateFromTemplateClick,
    resourceTemplates,
    calibration?.calibration,
    enableWedgeDrawingV2,
  ]);

  const getCopyShapeMutationInput = useCallback(
    (
      shape: SheetShapeDeepFragment,
      translationVector: Point2,
      newName: string
    ) => {
      if (shape.sheetShapePolygon) {
        const newMultipolygon: MultiPolygon2Type = JSON.parse(
          JSON.stringify(shape.sheetShapePolygon.multipolygon)
        );
        newMultipolygon.polygons.forEach((polygon) => {
          polygon.exterior.points.forEach((point) => {
            point.x += translationVector[0];
            point.y += translationVector[1];
          });
          polygon.interiors.forEach((interior) => {
            interior.points.forEach((point) => {
              point.x += translationVector[0];
              point.y += translationVector[1];
            });
          });
        });

        return getCreatePolygonInput(
          {
            valid: true,
            multipolygon: toMultiPolygon2(newMultipolygon),
          },
          projectId,
          shape.sheetId,
          shape.sheetPageNumber,
          newName,
          shape.folder ?? undefined
        );
      } else if (shape.sheetShapeLine) {
        const newPoints = shape.sheetShapeLine.points.map((point) => ({
          x: point.x + translationVector[0],
          y: point.y + translationVector[1],
        }));

        return getCreateLineInput(
          {
            valid: true,
            points: newPoints.map((point) => [point.x, point.y]),
          },
          projectId,
          shape.sheetId,
          shape.sheetPageNumber,
          newName,
          shape.folder ?? undefined
        );
      } else if (shape.sheetShapePoint) {
        return getCreatePointInput(
          {
            valid: true,
            points: [
              [
                shape.sheetShapePoint.point.x + translationVector[0],
                shape.sheetShapePoint.point.y + translationVector[1],
              ],
            ],
          },
          projectId,
          shape.sheetId,
          shape.sheetPageNumber,
          newName,
          shape.folder ?? undefined
        );
      }

      return null;
    },
    [projectId]
  );

  const { projectName, orderName } = useBreadcrumb();

  const moveOrCopyShapes = useCallback(
    // eslint-disable-next-line complexity
    async (
      shapeIds: string[],
      translationVector: Point2,
      shouldCopy = false
    ) => {
      if (!shapes) {
        console.error('Shapes not loaded successfully');
        return;
      }

      let updatedExistingNames = shapes.map((shape) => shape.name);

      const shapeOperations = [];

      for (const shapeId of shapeIds) {
        const shapeToProcess = shapes.find((shape) => shape.id === shapeId);
        if (!shapeToProcess) {
          console.error('Shape not found');
          continue;
        }

        if (shouldCopy) {
          // Generate a unique new name for the copied shape
          const newName = findNextAvailableNameForCopy(
            shapeToProcess.name,
            updatedExistingNames
          );
          updatedExistingNames.push(newName); // Update the list of existing names to include this new name

          // Assume we have a function to prepare the shape data for copying
          // which adjusts the shape's position based on the translationVector and assigns the new name.
          const shapeCopyData = getCopyShapeMutationInput(
            shapeToProcess,
            translationVector,
            newName
          );

          if (shapeCopyData) {
            // Add the copy operation promise to the list
            shapeOperations.push(
              createShapeMutation({
                input: Array.isArray(shapeCopyData)
                  ? shapeCopyData[0]
                  : shapeCopyData,
              })
            );
          }
        } else {
          // For moving, update the shape's position based on the translationVector.
          // Assume getUpdateShapeData returns the necessary mutation input for the move.
          const shapeMoveData = getMoveShapeMutationInput(
            shapeToProcess,
            translationVector
          );

          if (shapeMoveData) {
            // Add the move operation promise to the list
            shapeOperations.push(updateShapeMutation({ input: shapeMoveData }));
          }
        }
      }

      isMovingOrCopyingToastRef.current = toast({
        title: shouldCopy ? t('shapes.copying') : t('shapes.moving'),
        status: 'info',
      });

      // Execute all shape operations (copy or move)
      try {
        await Promise.all(shapeOperations);
        toast({
          status: 'success',
          title: t('shapes.success.title'),
          description: shouldCopy
            ? t('shapes.success.copy')
            : t('shapes.success.move'),
        });
      } catch (error) {
        toast({
          status: 'error',
          title: t('shapes.error.title'),
          description: shouldCopy
            ? t('shapes.error.copy')
            : t('shapes.error.move'),
        });
        throw error;
        // Additional error handling logic could be implemented here, such as reverting partially completed operations
      } finally {
        toast.close(isMovingOrCopyingToastRef.current);
        // Reset the move/copy states after the operation is complete
        setMovingShapes(false);
        setCopyingShapes(false);

        logEvent({
          eventName: shouldCopy ? 'Shapes Copied' : 'Shapes Moved',
          eventType: shouldCopy ? EventType.Created : EventType.Modified,
          resourceName: orderName || '',
          resourceType: EventResource.Shape,
          metadata: {
            affectedResources: shapeIds,
            details: shouldCopy
              ? `Copied ${shapeIds.length} shapes`
              : `Moved ${shapeIds.length} shapes`,
          },
        });
      }
    },
    [
      shapes,
      toast,
      t,
      getCopyShapeMutationInput,
      createShapeMutation,
      updateShapeMutation,
      logEvent,
      orderName,
    ]
  );

  function getMoveShapeMutationInput(
    shape: SheetShapeDeepFragment,
    translationVector: Point2
  ) {
    let result = null;
    if (shape.sheetShapePolygon) {
      const newMultipolygon: MultiPolygon2Type = JSON.parse(
        JSON.stringify(shape.sheetShapePolygon.multipolygon)
      ); // Deep copy to avoid mutation

      newMultipolygon.polygons.forEach((polygon) => {
        polygon.exterior.points.forEach((point) => {
          point.x += translationVector[0];
          point.y += translationVector[1];
        });
        polygon.interiors.forEach((interior) => {
          interior.points.forEach((point) => {
            point.x += translationVector[0];
            point.y += translationVector[1];
          });
        });
      });

      result = getUpdatePolygonInput(shape, {
        valid: true,
        multipolygon: toMultiPolygon2(newMultipolygon),
      });
    } else if (shape.sheetShapeLine) {
      const newPoints = shape.sheetShapeLine.points.map((point) => ({
        x: point.x + translationVector[0],
        y: point.y + translationVector[1],
      }));

      result = getUpdateLineInput(shape, {
        valid: true,
        points: newPoints.map((point) => [point.x, point.y]),
      });
    } else if (shape.sheetShapePoint) {
      result = getUpdatePointInput(shape, {
        valid: true,
        points: [
          [
            shape.sheetShapePoint.point.x + translationVector[0],
            shape.sheetShapePoint.point.y + translationVector[1],
          ],
        ],
      });
    }
    return result;
  }

  const { trackEvent } = useMixpanel();

  //Some of below states can likely be isolated to the AutogeneratedRoof provider
  const {
    automaticDrainAndWedgesPlacement,
    segments1,
    segments2,
    diamondsLine1,
    diamondsLine2,
    kilerLine1,
    kilerLine2,
    handleSubmitDrains,
    handleSubmitWedges,
  } = useAutoGeneratedRoof();

  const { handleUseFlatInsulation } = useInsulationHandler(
    order.order,
    activateDrawing
  );

  const submitDrawing = useCallback(
    async (skipProtanFlow = false) => {
      if (!drawingResult || !drawingResult.valid) {
        throw new Error('No result to submit');
      }
      if (shapeIdToEdit) {
        if (viewerMode === ViewerMode.SheetsV2) {
          await sheetsViewerV2Drawing.updateShape();
        } else {
          await updateShape();
        }
      } else {
        await createShapes();
        if (isProtan && !skipProtanFlow) {
          handleProtanFlow(
            activateDrawing,
            projectSteps,
            setGhostMode,
            setStepStatus,
            fireRequirements,
            handleUseFlatInsulation
          );

          if (automaticDrainAndWedgesPlacement && roofOutlineInProgress) {
            if (
              activeSheetId &&
              kilerLine1 &&
              kilerLine2 &&
              resourceTemplates
            ) {
              handleSubmitDrains(projectId, activeSheetId, pageNumber);
              handleSubmitWedges(
                segments1,
                projectId,
                activeSheetId,
                pageNumber,
                diamondsLine1,
                kilerLine1
              );
              handleSubmitWedges(
                segments2,
                projectId,
                activeSheetId,
                pageNumber,
                diamondsLine2,
                kilerLine2
              );
              if (orderId) {
                const uniqueKileSkus = new Set(kilerLine1.concat(kilerLine2));
                uniqueKileSkus.forEach((sku) => {
                  if (order.resolvedOrder?.orderEntries)
                    kileChoices(
                      sku,
                      orderId,
                      handleCreateFromTemplateClick,
                      resourceTemplates,
                      order.resolvedOrder.orderEntries
                    );
                });
              }
              setStepStatus(
                ProtanProjectStepEnum.DrainPlacement,
                ProtanProjectStepStatusEnum.Completed
              );
              deactivateDrawing();
            }
          }
        }

        trackEvent(EventName.ShapeCreated, {
          'Viewer Mode': ViewerMode.Sheets,
          'Shape Type': drawingResult.type,
          'Project Name': projectName,
          'Project ID': projectId,
          'Table Name': orderName,
        });
      }
    },
    [
      drawingResult,
      shapeIdToEdit,
      updateShape,
      createShapes,
      isProtan,
      trackEvent,
      projectName,
      projectId,
      orderName,
      activateDrawing,
      projectSteps,
      setGhostMode,
      setStepStatus,
      fireRequirements,
      handleUseFlatInsulation,
      automaticDrainAndWedgesPlacement,
      roofOutlineInProgress,
      activeSheetId,
      kilerLine1,
      kilerLine2,
      resourceTemplates,
      handleSubmitDrains,
      pageNumber,
      handleSubmitWedges,
      segments1,
      diamondsLine1,
      segments2,
      diamondsLine2,
      orderId,
      deactivateDrawing,
      order?.resolvedOrder?.orderEntries,
      handleCreateFromTemplateClick,
      viewerMode,
      sheetsViewerV2Drawing,
    ]
  );

  useEffect(() => {
    if (shapes) {
      const shapesForSheetAndPage = shapes
        .filter(
          (shape) =>
            shape.sheetId === activeSheetId &&
            shape.sheetPageNumber === pageNumber
        )
        .sort((a, b) => {
          const getPriority = (shape: SheetShapeDeepFragment) => {
            if (shape.sheetShapePoint) return 2; // Highest priority (render last)
            if (shape.sheetShapeLine) return 1; // Next highest priority
            return 0; // Default priority (render first)
          };
          return getPriority(a) - getPriority(b);
        });
      const shapesByUrn = groupBy(shapesForSheetAndPage, (shape) => shape.urn);
      for (const urn of Object.keys(shapesByUrn)) {
        renderSheetShapes(urn, shapesByUrn[urn]);
      }
      return () => {
        for (const urn of Object.keys(shapesByUrn)) {
          unrenderSheetShapes(urn);
        }
      };
    }
  }, [
    activeSheetId,
    renderSheetShapes,
    pageNumber,
    shapes,
    unrenderSheetShapes,
  ]);

  // Hook for deactivating drawing if the shape to edit is deleted
  useEffect(() => {
    if (shapes !== null) {
      const shapeIds = shapes.map((shape) => shape.id);
      if (
        isDrawingShape &&
        shapeIdToEdit &&
        !shapeIds.includes(shapeIdToEdit)
      ) {
        deactivateDrawing();
      }
    }
  }, [deactivateDrawing, isDrawingShape, shapeIdToEdit, shapes]);

  // Hook for hiding the shape that is being edited
  useEffect(() => {
    if (shapeToEdit && isDrawingShape && shapeIdToEdit) {
      hideSheetShape(shapeToEdit.urn, shapeToEdit.dbId);
      return () => {
        if (shapeToEdit) {
          showSheetShape(shapeToEdit.urn, shapeToEdit.dbId);
        }
      };
    }
  }, [
    isDrawingShape,
    shapeToEdit,
    shapeIdToEdit,
    viewerMode,
    hideSheetShape,
    showSheetShape,
  ]);

  useEffect(() => {
    if (viewerMode === ViewerMode.SheetsV2) {
      setDrawingResult(
        sheetsViewerV2Drawing.results as unknown as ShapeDrawingResult
      );
    }
  }, [sheetsViewerV2Drawing.results, viewerMode]);

  // Sync v2 viewer's shapeToEdit with main shapeIdToEdit
  useEffect(() => {
    if (viewerMode === ViewerMode.SheetsV2) {
      if (!sheetsViewerV2Drawing.shapeToEdit && shapeIdToEdit) {
        setShapeIdToEdit(null);
      }
    }
  }, [
    sheetsViewerV2Drawing.shapeToEdit,
    shapeIdToEdit,
    viewerMode,
    setShapeIdToEdit,
  ]);

  // Hook for setting cursor when moving or copying shapes
  useEffect(() => {
    const moveOrCopyCursor = `url(/src/assets/icons/${
      isCopyingShapes ? 'copy' : 'move'
    }-shape-icon.png), crosshair`;

    if (isMovingShapes || isCopyingShapes) {
      viewerRef?.current?.style.setProperty('cursor', moveOrCopyCursor);
    } else {
      viewerRef?.current?.style.removeProperty('cursor');
    }
  }, [isCopyingShapes, isMovingShapes, viewerRef]);

  const drawingState: SheetShapeDrawingState = useMemo(() => {
    if (!isDrawingShape) {
      return {
        isDrawing: false,
        hasResult: false,
      };
    } else if (drawingMode === ShapeDrawingMode.DrainLine) {
      return {
        isDrawing: true,
        hasResult: hasDrawingResult,
        mode: drawingMode,
        minHeight,
      };
    } else {
      return {
        isDrawing: true,
        hasResult: hasDrawingResult,
        mode: drawingMode,
      };
    }
  }, [drawingMode, hasDrawingResult, isDrawingShape, minHeight]);

  const { selectedSheetShapes } = useSelection();

  const selectedShapes = useMemo(() => {
    const filtered =
      shapes?.filter((shape) => isShapeSelected(shape, selectedSheetShapes)) ??
      [];
    return filtered;
  }, [selectedSheetShapes, shapes]);

  // Custom setMovingShapes that captures selection when activated
  const setMovingShapesWithCapture = useCallback(
    (isMoving: boolean) => {
      if (isMoving) {
        // Capture the current selection when activating move mode
        setCapturedShapesForMove(selectedShapes);
      } else {
        // Clear captured shapes when deactivating
        setCapturedShapesForMove([]);
      }
      setMovingShapes(isMoving);
    },
    [selectedShapes]
  );

  // Custom setCopyingShapes that captures selection when activated
  const setCopyingShapesWithCapture = useCallback(
    (isCopying: boolean) => {
      if (isCopying) {
        // Capture the current selection when activating copy mode
        setCapturedShapesForMove(selectedShapes);
      } else {
        // Clear captured shapes when deactivating
        setCapturedShapesForMove([]);
      }
      setCopyingShapes(isCopying);
    },
    [selectedShapes]
  );

  const contextValue = useMemo<SheetShapesContextValue>(
    () => ({
      drawing: {
        activate: activateDrawing,
        editShape,
        moveOrCopyShapes,
        deactivate: deactivateDrawing,
        submit: submitDrawing,
        submitError: createShapeResult.error || updateShapeResult.error,
        isSubmitting,
        shapeToEdit: shapeIdToEdit,
        isMovingShapes: isMovingShapes,
        setMovingShapes: setMovingShapesWithCapture,
        isCopyingShapes: isCopyingShapes,
        setCopyingShapes: setCopyingShapesWithCapture,
        setMinHeight: setMinHeight,
        ...drawingState,
      },
      shapes: {
        data: shapes ?? undefined,
        fetching: shapesFetching,
        error: shapesError,
      },
    }),
    [
      activateDrawing,
      editShape,
      moveOrCopyShapes,
      deactivateDrawing,
      submitDrawing,
      createShapeResult.error,
      updateShapeResult.error,
      isSubmitting,
      shapeIdToEdit,
      isMovingShapes,
      isCopyingShapes,
      setMovingShapesWithCapture,
      setCopyingShapesWithCapture,
      drawingState,
      shapes,
      shapesFetching,
      shapesError,
    ]
  );

  const onMoveOrCopyShapeResult = useCallback(
    (translationVector: Point2) =>
      moveOrCopyShapes(
        capturedShapesForMove.map((shape) => shape.id) as string[],
        translationVector,
        isCopyingShapes
      ),
    [isCopyingShapes, moveOrCopyShapes, capturedShapesForMove]
  );

  // Get the appropriate viewer reference based on viewer mode
  const getViewerRef = useCallback(() => {
    if (viewerMode === ViewerMode.SheetsV2) {
      // For v2, we need to check if the v2 viewer is available
      return true; // The MoveOrCopySheetShapes component will handle the v2 viewer internally
    } else {
      // For v1, use the viewerRef from useSheetViewer
      return viewerRef;
    }
  }, [viewerMode, viewerRef]);

  return (
    <SheetShapesContext.Provider value={contextValue}>
      {children}
      {isDrawingShape ? (
        <ShapeDrawing
          // https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
          key={drawingModeKey}
          mode={drawingMode}
          onResult={setDrawingResult}
          initialState={drawingInitialState}
          minHeight={minHeight}
        />
      ) : null}
      {getViewerRef() && (isMovingShapes || isCopyingShapes) ? (
        <MoveOrCopySheetShapes
          shapes={capturedShapesForMove}
          onResult={onMoveOrCopyShapeResult}
        />
      ) : null}
      <GeometricResultVisualization operationResults={operationResults} />
    </SheetShapesContext.Provider>
  );
};

export const useSheetShapes = () => {
  const context = useContext(SheetShapesContext);
  if (!context) {
    throw new Error('useShapesContext must be used within a ShapesProvider');
  }
  return context;
};

const getUpdateShapeInput = (
  shapeToEdit: SheetShapeDeepFragment,
  drawingResult: ShapeDrawingResult
): UpdateSheetShapeInput => {
  const resultType = drawingResult.type;

  if (resultType === ShapeDrawingResultType.Polygon) {
    return getUpdatePolygonInput(
      shapeToEdit,
      drawingResult as PolygonShapeDrawingResult
    );
  } else if (resultType === ShapeDrawingResultType.LineString) {
    return getUpdateLineInput(
      shapeToEdit,
      drawingResult as LineStringShapeDrawingResult
    );
  } else if (resultType === ShapeDrawingResultType.Point) {
    return getUpdatePointInput(
      shapeToEdit,
      drawingResult as PointDrawingResult
    );
  } else if (resultType === ShapeDrawingResultType.DrainLine) {
    return getUpdateDrainLineInput(
      shapeToEdit,
      drawingResult as DrainLineDrawingResult
    );
  } else if (resultType === ShapeDrawingResultType.Wedge) {
    return getUpdateWedgeInput(
      shapeToEdit,
      drawingResult as WedgesDrawingResult
    );
  } else {
    throw new Error(`Unknown result type ${resultType}`);
  }
};

const getMultipolygonFromDrawingResult = (
  drawingResult: PolygonShapeDrawingResult
): MultiPolygon2Type => {
  invariant(drawingResult);
  invariant(drawingResult.valid);
  return {
    polygons: drawingResult.multipolygon.polygons.map((polygon) => ({
      exterior: {
        points: polygon.exterior.map((point) => ({
          x: point[0],
          y: point[1],
        })),
      },
      interiors: polygon.interiors.map((interior) => ({
        points: interior.map((point) => ({
          x: point[0],
          y: point[1],
        })),
      })),
    })),
  };
};

const getUpdatePolygonInput = (
  shapeToEdit: SheetShapeDeepFragment,
  drawingResult: PolygonShapeDrawingResult
): UpdateSheetShapeInput => {
  invariant(shapeToEdit, 'Shape to edit is null');
  invariant(drawingResult);
  invariant(drawingResult.valid);

  return {
    id: shapeToEdit.id,
    patch: {
      sheetShapePolygon: {
        updateById: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: shapeToEdit.sheetShapePolygon!.id,
          patch: {
            multipolygon: getMultipolygonFromDrawingResult(drawingResult),
          },
        },
      },
    },
  };
};

const getUpdateLineInput = (
  shapeToEdit: SheetShapeDeepFragment,
  drawingResult: LineStringShapeDrawingResult
): UpdateSheetShapeInput => {
  invariant(shapeToEdit, 'Shape to edit is null');
  return {
    id: shapeToEdit.id,
    patch: {
      sheetShapeLine: {
        updateById: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: shapeToEdit.sheetShapeLine!.id,
          patch: {
            points: drawingResult.points.map((point) => ({
              x: point[0],
              y: point[1],
            })),
          },
        },
      },
    },
  };
};

const getUpdateDrainLineInput = (
  shapeToEdit: SheetShapeDeepFragment,
  drawingResult: DrainLineDrawingResult
): UpdateSheetShapeInput => {
  invariant(shapeToEdit, 'Shape to edit is null');
  return {
    id: shapeToEdit.id,
    patch: {
      sheetShapeSplitLine: {
        updateById: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: shapeToEdit.sheetShapeSplitLine!.id,
          patch: {
            // Add each line segment as pairs of points
            points: drawingResult.drainLines.flatMap((line) => [
              { x: line[0][0], y: line[0][1] },
              { x: line[1][0], y: line[1][1] },
            ]),
          },
        },
      },
    },
  };
};

const getUpdatePointInput = (
  shapeToEdit: SheetShapeDeepFragment,
  drawingResult: PointDrawingResult
): UpdateSheetShapeInput => {
  invariant(shapeToEdit, 'Shape to edit is null');
  invariant(drawingResult.points[0], 'Point is null');
  return {
    id: shapeToEdit.id,
    patch: {
      sheetShapePoint: {
        updateById: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: shapeToEdit.sheetShapePoint!.id,
          patch: {
            point: {
              x: drawingResult.points[0][0],
              y: drawingResult.points[0][1],
            },
          },
        },
      },
    },
  };
};

const getUpdateWedgeInput = (
  shapeToEdit: SheetShapeDeepFragment,
  drawingResult: WedgesDrawingResult
): UpdateSheetShapeInput => {
  invariant(shapeToEdit.sheetShapeLine, 'Expected sheet shape line');

  // We're updating an existing SheetShapeLine with the new wedge points
  return {
    id: shapeToEdit.id,
    patch: {
      sheetShapeLine: {
        updateById: {
          id: shapeToEdit.sheetShapeLine.id,
          patch: {
            points: drawingResult.points.map((point) => ({
              x: point[0],
              y: point[1],
            })),
          },
        },
      },
    },
  };
};

function getCreateShapeInput(
  drawingResult: ShapeDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  enableWedgeDrawingV2 = false
): CreateSheetShapeInput | CreateSheetShapeInput[] {
  switch (drawingResult.type) {
    case ShapeDrawingResultType.Polygon:
      return getCreatePolygonInput(
        drawingResult as PolygonShapeDrawingResult,
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName
      );
    case ShapeDrawingResultType.LineString:
      return getCreateLineInput(
        drawingResult as LineStringShapeDrawingResult,
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName
      );
    case ShapeDrawingResultType.Point:
      return getCreatePointInput(
        drawingResult as PointDrawingResult,
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName
      );
    case ShapeDrawingResultType.Wedge: {
      if (enableWedgeDrawingV2) {
        return getCreateWedgeInputV2(
          drawingResult as WedgesDrawingResult,
          projectId,
          sheetId,
          sheetPageNumber,
          shapeName
        );
      } else {
        return getCreateWedgeInput(
          drawingResult as WedgesDrawingResult,
          projectId,
          sheetId,
          sheetPageNumber,
          shapeName
        );
      }
    }
    case ShapeDrawingResultType.DrainLine:
      return getCreateDrainLineInput(
        drawingResult as DrainLineDrawingResult,
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName
      );
  }
}

const getCreatePolygonInput = (
  drawingResult: PolygonShapeDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folderId?: string
): CreateSheetShapeInput => {
  invariant(drawingResult);
  invariant(drawingResult.valid);
  return {
    sheetShape: {
      projectId,
      sheetId,
      urn: getSheetShapeUrn(projectId),
      sheetPageNumber,
      name: shapeName,
      sheetShapePolygon: {
        create: [
          {
            multipolygon: getMultipolygonFromDrawingResult(drawingResult),
          },
        ],
      },
    },
  };
};

const getCreateLineInput = (
  drawingResult: LineStringShapeDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folderId?: string
): CreateSheetShapeInput => {
  return {
    sheetShape: {
      projectId,
      sheetId,
      urn: getSheetShapeUrn(projectId),
      sheetPageNumber,
      name: shapeName,
      sheetShapeLine: {
        create: [
          {
            points: drawingResult.points.map((point) => ({
              x: point[0],
              y: point[1],
            })),
          },
        ],
      },
    },
  };
};

const getCreateDrainLineInput = (
  drawingResult: DrainLineDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folderId?: string
): CreateSheetShapeInput => {
  return {
    sheetShape: {
      projectId,
      sheetId,
      urn: getSheetShapeUrn(projectId),
      sheetPageNumber,
      name: shapeName,
      sheetShapeSplitLine: {
        create: [
          {
            points: drawingResult.drainLines.flatMap((line) => [
              { x: line[0][0], y: line[0][1] },
              { x: line[1][0], y: line[1][1] },
            ]),
          },
        ],
      },
    },
  };
};

const getCreateWedgeInput = (
  drawingResult: WedgesDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folderId?: string
): CreateSheetShapeInput[] => {
  const lineSegments: Point2[][] = drawingResult.points
    .map((point, index) => {
      if (index < drawingResult.points.length - 1) {
        return [drawingResult.points[index], drawingResult.points[index + 1]];
      }
      return null;
    })
    .filter((segment): segment is Point2[] => segment !== null);

  return lineSegments.map((lineSegment) => {
    return {
      sheetShape: {
        projectId,
        sheetId,
        urn: getSheetShapeUrn(projectId),
        sheetPageNumber,
        name: shapeName,
        sheetShapeLine: {
          create: [
            {
              points: [
                { x: lineSegment[0][0], y: lineSegment[0][1] },
                { x: lineSegment[1][0], y: lineSegment[1][1] },
              ],
            },
          ],
        },
      },
    };
  });
};

// Count wedges by SKU
const countWedgesBySku = (
  drawingResult: WedgesDrawingResult
): Record<KileTypeSKU, number> => {
  // Initialize counters for each SKU type
  const skuCounts: Record<KileTypeSKU, number> = Object.values(
    KileTypeSKU
  ).reduce((acc, sku) => {
    acc[sku] = 0;
    return acc;
  }, {} as Record<KileTypeSKU, number>);

  // Count wedges for each kileType based on diamond settings
  drawingResult.kileType.forEach((sku, index) => {
    if (index < drawingResult.diamond.length) {
      const diamond = drawingResult.diamond[index];
      // Count how many triangles are visible in this diamond
      let visibleTriangles = 0;
      if (diamond.triangle1) visibleTriangles++;
      if (diamond.triangle2) visibleTriangles++;
      if (diamond.triangle3) visibleTriangles++;
      if (diamond.triangle4) visibleTriangles++;

      // Increment the counter for this SKU by the number of visible triangles
      skuCounts[sku] += visibleTriangles;
    }
  });

  return skuCounts;
};

// New V2 version that keeps polyline intact and adds SKU attributes
const getCreateWedgeInputV2 = (
  drawingResult: WedgesDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folderId?: string
): CreateSheetShapeInput => {
  // Create a polyline with all points
  return {
    sheetShape: {
      projectId,
      sheetId,
      urn: getSheetShapeUrn(projectId),
      sheetPageNumber,
      name: shapeName,
      sheetShapeLine: {
        create: [
          {
            points: drawingResult.points.map((point) => ({
              x: point[0],
              y: point[1],
            })),
          },
        ],
      },
    },
  };
};

export const getCreatePointInput = (
  drawingResult: PointDrawingResult,
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folderId?: string
): CreateSheetShapeInput[] => {
  invariant(drawingResult.points, 'Point is null');

  return drawingResult.points.map((point) => {
    return {
      sheetShape: {
        projectId,
        sheetId,
        urn: getSheetShapeUrn(projectId),
        sheetPageNumber,
        name: shapeName,
        sheetShapePoint: {
          create: [
            {
              point: {
                x: point[0],
                y: point[1],
              },
            },
          ],
        },
      },
    };
  });
};
